// Inicialización y helpers de Sentry para captura de errores y trazas
// de rendimiento. Todo el reporte es opt-in vía `__SENTRY_DSN__`: si está
// vacío, Sentry no arranca y los wrappers son no-op (ideal para dev local
// sin contaminar el dashboard).
//
// Privacidad: igual que Analytics, no enviamos PII. La opción
// `sendDefaultPii: false` desactiva la captura automática de IPs,
// User-Agent y nombres de host por usuario.
import * as Sentry from '@sentry/react';
import { telemetryError } from './telemetry';

let initialized = false;

export function initErrorTracking(): void {
  if (initialized) return;
  if (!__SENTRY_DSN__) return;
  Sentry.init({
    dsn: __SENTRY_DSN__,
    environment: __SENTRY_ENVIRONMENT__,
    release: `aldahra-pwa@${__APP_VERSION__}+${__BUILD_TIME__}`,
    // Sample 100% de errores; performance al 10% para no quemar cuota
    // del tier gratuito (10k events/mes).
    tracesSampleRate: 0.1,
    // No enviamos IPs, User-Agent agregable, etc. Reducimos huella.
    sendDefaultPii: false,
    // Filtros: ignoramos ruido típico de navegador que no nos aporta nada.
    ignoreErrors: [
      // ResizeObserver loop spam — no es un bug real, lo ignoran todos.
      'ResizeObserver loop completed with undelivered notifications',
      'ResizeObserver loop limit exceeded',
      // Errores de extensiones del navegador inyectando en window.
      /chrome-extension:\/\//,
      /moz-extension:\/\//,
      /safari-extension:\/\//,
      // Errores de carga de chunks por despliegue concurrente — molestos
      // pero esperables y se resuelven con un refresh.
      'Loading chunk',
      'Loading CSS chunk',
      // Fallos al registrar el Service Worker. Suelen venir de extensiones
      // del navegador, antivirus o navegadores en modo estricto que
      // envuelven o bloquean navigator.serviceWorker. No es código nuestro
      // ni hay nada accionable: la app web sigue funcionando, solo se
      // pierde el modo offline para ese usuario.
      /registerSW\.js/,
      /serviceWorker\.register/,
      // iOS Safari standalone lanza SecurityError al intentar refrescar el
      // SW si la red falla justo en ese momento. Es transitorio y se
      // reintenta solo en el siguiente ciclo. No es accionable: el SW
      // antiguo sigue funcionando hasta que la red estable y el navegador
      // vuelve a intentar la descarga.
      /SecurityError.*Script.*sw\.js.*load failed/i,
      /Script .*\/sw\.js.* load failed/i,
      // Chrome/Android: la red se cayó mientras descargaba el script del SW.
      // El registro que genera vite-plugin-pwa no lleva catch, así que sale
      // como promesa no capturada. Es transitorio (el navegador reintenta en
      // la siguiente visita) y mientras tanto la web funciona sin offline.
      //
      // Se filtra SOLO esta variante, la de red. Los demás fallos de registro
      // delatan un despliegue roto —MIME type 'text/html' porque el servidor
      // devolvió el index, 404, o un error al evaluar nuestro sw.ts— y esos
      // SÍ tienen que llegar a Sentry.
      /Failed to register a ServiceWorker.*An unknown error occurred when fetching the script/i,
      // La ACTUALIZACIÓN del SW falla porque la red se cayó justo entonces
      // (Egipto, sep 2026). El SW vigente sigue sirviendo; el navegador
      // reintenta en la próxima visita. Misma familia que el registro.
      /Failed to update a ServiceWorker/i,
      // Canal HMR del servidor de desarrollo de Vite. No existe en producción,
      // pero cuando se desarrolla sobre master el build lleva environment
      // "production" y estos eventos se colaban como si fueran reales.
      'send was called before connect',
    ],
    // El "Error: Rejected" que el navegador lanza desde el script de registro
    // del SW (visitas a www.aldahracontrollers.com sin sesión). Sin usuario,
    // sin pila útil y sin nada que arreglar en la app: se filtra por origen.
    denyUrls: [/registerSW\.js/],
    // Qué se envía y qué se descarta vive en debeReportarse (abajo), aparte
    // para poder testearlo.
    beforeSend(event, hint) {
      // Lo que pasa en el portátil de desarrollo no es un error de producción,
      // aunque el build diga "production" por venir de master: el desarrollador
      // ya lo está viendo en su consola. Sin esto, cada prueba local (HMR,
      // usuarios de test, un rol a medio implementar) abría issues en el
      // proyecto de producción y tapaba los de los controladores.
      if (esEntornoLocal(window.location.hostname)) return null;
      return debeReportarse(hint.originalException, event.tags?.offline_sync === 'true')
        ? event
        : null;
    },
  });
  initialized = true;
}

// ── Qué llega a Sentry ──────────────────────────────────────────────────────
// Decide si un error merece un evento en Sentry. Extraído del `beforeSend`
// para poder cubrirlo con tests: es la lógica que determina qué vemos y qué no
// de producción, y una "simplificación" descuidada aquí nos deja ciegos.
//
// `esOfflineSync` = el evento lleva el tag `offline_sync` (lo pone
// captureSyncError): fallos de la cola al subir cargas o fotos.
//
// La telemetría del visor NO pasa por aquí: allí se registra todo, siempre.
export function debeReportarse(error: unknown, esOfflineSync: boolean): boolean {
  // Cancelación propia (el usuario pulsó "trabajar offline" y el interceptor
  // abortó la petición): no ha fallado nada, solo se ha pospuesto. Se
  // comprueba lo PRIMERO, incluso antes que la cola: si el modo se activa
  // mientras está subiendo, cada item en vuelo generaría un evento.
  const cancel = error as { name?: string; code?: string } | null | undefined;
  if (cancel?.code === 'ERR_CANCELED' || cancel?.name === 'CanceledError') return false;

  const esAxios = !!error && typeof error === 'object' && 'isAxiosError' in error;
  // Lo que no es de axios (IndexedDB, lease, bugs nuestros) se reporta
  // siempre: ahí no hay red a la que culpar.
  if (!esAxios) return true;
  const ax = error as { response?: { status?: number } };
  const status = ax.response?.status;

  // Sin respuesta del servidor → red caída, timeout o lie-fi. En una PWA de
  // campo con cobertura mala eso es funcionamiento normal, no un bug: la cola
  // reintenta sola y el trabajo acaba llegando. Generaba avisos recurrentes
  // que nunca requerían acción (controlador de Egipto, sep 2026). Se sigue
  // viendo entero en el visor, que es donde se diagnostica una cola que no
  // drena.
  if (!ax.response) return false;

  // A partir de aquí el servidor SÍ respondió.
  //
  // La cola pasa con cualquier código: un rechazo del servidor a una carga o
  // foto encolada siempre es algo que arreglar, incluido el 4xx por payload
  // inválido y el 5xx transitorio. Así se cazó el 500 repetido de Egipto del
  // 4 sep 2026.
  if (esOfflineSync) return true;

  // Fuera de la cola somos más estrictos:
  // - 401 lo gestiona el AuthContext (logout). No es un bug.
  // - 4xx son errores de datos o de permisos, no de la app.
  // - 502/503/504 son indisponibilidad transitoria del proxy o del backend
  //   (reinicio, cold start, saturación); la UI ya degrada sola.
  // Queda el 500 "real" del backend, que sí interesa.
  if (status != null && status < 500) return false;
  if (status != null && [502, 503, 504].includes(status)) return false;
  return true;
}

// Un mismo error relanzado en bucle (la apertura de IndexedDB en un
// dispositivo sin almacenamiento, ver getDB en db.ts) se registra UNA vez —en
// Sentry y en el visor— y las repeticiones se callan. Quien relanza pone la
// marca `yaReportado`; la primera captura lo ve y lo deja como ya visto.
export function esRepeticion(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { yaReportado?: boolean; _capturado?: boolean };
  if (!e.yaReportado) return false;
  if (e._capturado) return true;
  e._capturado = true;
  return false;
}

// ¿Es un host de desarrollo? localhost, loopback, .local y las redes privadas
// (la PWA se prueba desde el móvil contra la IP del portátil con --host).
export function esEntornoLocal(hostname: string): boolean {
  const h = (hostname || '').toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h === '::1' || h === '[::1]' || h.endsWith('.local')) return true;
  if (/^10\.\d+\.\d+\.\d+$/.test(h)) return true;
  if (/^192\.168\.\d+\.\d+$/.test(h)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/.test(h)) return true;
  return false;
}

export function setErrorTrackingUser(username: string | null): void {
  if (!initialized) return;
  if (username) {
    // Pasamos solo un identificador anónimo, no el email real.
    Sentry.setUser({ username });
  } else {
    Sentry.setUser(null);
  }
}

export function captureError(error: unknown, context?: Record<string, unknown>): void {
  if (esRepeticion(error)) return;
  // La telemetría en vivo va SIEMPRE, aunque Sentry no esté inicializado: son
  // dos canales con propósitos distintos —Sentry es el registro permanente y
  // el visor es lo que permite ver qué le pasa AHORA a una tablet en el campo.
  telemetryError(typeof context?.where === 'string' ? context.where : 'app', error, context);
  if (!initialized) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

// Captura específica para fallos durante la sincronización offline.
// Aplica el tag `offline_sync=true` que el beforeSend respeta para que
// NO se filtren — vemos cualquier error real (red, 4xx, IDB, etc.) que
// impida que una carga o foto encolada llegue al servidor.
export function captureSyncError(error: unknown, context?: Record<string, unknown>): void {
  if (esRepeticion(error)) return;
  // El CUERPO de la respuesta del servidor viaja con el evento. Un 500 sin el
  // texto que devolvió la API no se puede diagnosticar desde fuera: el 4 sep
  // 2026 un NEW de Egipto devolvía 500 en cada reintento y ni Sentry ni el
  // visor decían por qué (la API no tenía App Insights ni logs activos).
  // Recortado: un HTML de error de IIS puede pesar KBs.
  const enriched = { ...(context ?? {}), ...describeHttpResponse(error) };
  // Igual que captureError: el visor en vivo es justo donde se diagnostica una
  // cola que no drena, así que el evento va aunque Sentry esté apagado.
  telemetryError(typeof context?.stage === 'string' ? context.stage : 'sync', error, enriched);
  if (!initialized) return;
  Sentry.withScope((scope) => {
    scope.setTag('offline_sync', 'true');
    scope.setExtras(enriched);
    // Si el error es un AxiosError, exponemos status como tag para
    // facilitar agrupación en Sentry.
    if (error && typeof error === 'object' && 'isAxiosError' in error) {
      const ax = error as { response?: { status?: number }; code?: string };
      if (ax.response?.status != null) {
        scope.setTag('http_status', String(ax.response.status));
      }
      if (ax.code) {
        scope.setTag('axios_code', ax.code);
      }
    }
    Sentry.captureException(error);
  });
}

// Extrae status + cuerpo (recortado) de la respuesta de un AxiosError. Vacío
// si no hay respuesta (red caída, timeout, cancelación) o no es de axios.
const RESPONSE_BODY_MAX = 400;
export function describeHttpResponse(error: unknown): Record<string, unknown> {
  if (!error || typeof error !== 'object' || !('isAxiosError' in error)) return {};
  const ax = error as { response?: { status?: number; data?: unknown }; config?: { url?: string; method?: string } };
  const out: Record<string, unknown> = {};
  if (ax.config?.url) out.httpUrl = `${(ax.config.method ?? '').toUpperCase()} ${ax.config.url}`;
  if (!ax.response) return out;
  if (ax.response.status != null) out.httpStatus = ax.response.status;
  const data = ax.response.data;
  if (data == null || data === '') return out;
  let text: string;
  if (typeof data === 'string') text = data;
  else {
    try { text = JSON.stringify(data); } catch { text = String(data); }
  }
  // Un HTML de error (IIS/APIM) no aporta nada legible: nos quedamos con el título.
  const title = /<title>([^<]*)<\/title>/i.exec(text)?.[1];
  if (title) text = `[html] ${title}`;
  out.httpBody = text.length > RESPONSE_BODY_MAX ? `${text.slice(0, RESPONSE_BODY_MAX)}…` : text;
  return out;
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
